import {
  skyFlightFrame,
  skyFlightPointToWorld,
  skyFlightVectorToWorld,
} from './92-sky-flight-frame.mjs';
import { prepareSkyLeviathanMesh } from './96-sky-leviathan-contacts.mjs';

// These bound asset-readiness work, not gameplay dimensions or collision policy.
export const SKY_RIGID_ASSEMBLY_LIMITS = Object.freeze({
  parts: 128,
  trianglesPerPart: 100000,
  triangles: 120000,
  coordinate: 150,
  restTolerance: 1e-4,
});
const axes = ['x', 'y', 'z'];
const frameKeys = ['origin', 'right', 'up', 'back'];
const accepted = new WeakSet();
const name = (value) =>
  typeof value === 'string' &&
  /^[\w:-]{1,100}$/.test(value) &&
  !['__proto__', 'prototype', 'constructor'].includes(value);
const finite = (point) =>
  point &&
  axes.every(
    (axis) =>
      Number.isFinite(point[axis]) && Math.abs(point[axis]) <= SKY_RIGID_ASSEMBLY_LIMITS.coordinate,
  );
const freezeFrame = (frame) =>
  Object.freeze(
    Object.fromEntries(
      frameKeys.map((key) => [
        key,
        Object.freeze(Object.fromEntries(axes.map((axis) => [axis, frame[key][axis]]))),
      ]),
    ),
  );

/** Validate and copy a finite rigid assembly schema. Rest frames map each joint
 * into its named parent (or asset root), in metres. An optional driver mapping
 * must specify both driverId and driverToJoint: the latter maps art-joint local
 * coordinates into the canonical driver's coordinates. No offset is inferred.
 * Cyclic, ambiguous, nonrigid or incomplete schemas return null. */
export function skyRigidAssemblyDescriptors(source) {
  if (!Array.isArray(source) || !source.length || source.length > SKY_RIGID_ASSEMBLY_LIMITS.parts)
    return null;
  const ids = new Set(),
    nodes = new Set(),
    meshes = new Set(),
    result = [];
  for (const part of source) {
    if (
      !part ||
      ![part.id, part.node, part.mesh].every(name) ||
      ids.has(part.id) ||
      nodes.has(part.node) ||
      meshes.has(part.mesh)
    )
      return null;
    const restFrame = skyFlightFrame(part.restFrame);
    if (!restFrame || !finite(restFrame.origin)) return null;
    const hasDriver = part.driverId !== undefined || part.driverToJoint !== undefined;
    const driverToJoint = hasDriver && skyFlightFrame(part.driverToJoint);
    if (hasDriver && (!name(part.driverId) || !driverToJoint || !finite(driverToJoint.origin)))
      return null;
    const parentId = part.parentId ?? null;
    if (parentId !== null && !name(parentId)) return null;
    ids.add(part.id);
    nodes.add(part.node);
    meshes.add(part.mesh);
    result.push(
      Object.freeze({
        id: part.id,
        node: part.node,
        mesh: part.mesh,
        parentId,
        restFrame: freezeFrame(restFrame),
        ...(hasDriver
          ? { driverId: part.driverId, driverToJoint: freezeFrame(driverToJoint) }
          : {}),
      }),
    );
  }
  const byId = new Map(result.map((part) => [part.id, part]));
  for (const part of result) {
    const seen = new Set([part.id]);
    let parent = part.parentId;
    while (parent !== null) {
      if (!byId.has(parent) || seen.has(parent)) return null;
      seen.add(parent);
      parent = byId.get(parent).parentId;
    }
  }
  return Object.freeze(result);
}

/** Compile actual rigid surfaces once during asset readiness. Triangle vertices
 * are joint-local metres, copied without changing source buffers. The supplied
 * schema pins hierarchy/rest transforms; malformed, degenerate, oversized or
 * mismatched input returns null. Immutable facets and their acceleration index
 * are prepared before returning, so the first shot does not build a BVH. This
 * generic handle carries no permission to install it into a specific encounter. */
export function compileSkyRigidAssembly(parts, sourceDescriptors) {
  const descriptors = skyRigidAssemblyDescriptors(sourceDescriptors);
  if (!descriptors || !Array.isArray(parts) || parts.length !== descriptors.length) return null;
  const byId = new Map(descriptors.map((part) => [part.id, part]));
  const ids = new Set(),
    compiled = [],
    points = new WeakMap();
  let total = 0;
  const copy = (point) => {
    if (!finite(point)) return null;
    if (!points.has(point))
      points.set(point, Object.freeze({ x: point.x, y: point.y, z: point.z }));
    return points.get(point);
  };
  for (const part of parts) {
    const descriptor = part && byId.get(part.id);
    if (!descriptor || ids.has(part.id) || (part.parentId ?? null) !== descriptor.parentId)
      return null;
    const frame = skyFlightFrame(part.restFrame);
    if (
      !frame ||
      !finite(frame.origin) ||
      !Array.isArray(part.triangles) ||
      !part.triangles.length ||
      part.triangles.length > SKY_RIGID_ASSEMBLY_LIMITS.trianglesPerPart ||
      (total += part.triangles.length) > SKY_RIGID_ASSEMBLY_LIMITS.triangles
    )
      return null;
    if (
      frameKeys.some((key) =>
        axes.some(
          (axis) =>
            Math.abs(frame[key][axis] - descriptor.restFrame[key][axis]) >
            SKY_RIGID_ASSEMBLY_LIMITS.restTolerance,
        ),
      )
    )
      return null;
    ids.add(part.id);
    const triangles = [],
      low = { x: Infinity, y: Infinity, z: Infinity },
      high = { x: -Infinity, y: -Infinity, z: -Infinity };
    for (const [index, source] of part.triangles.entries()) {
      const a = copy(source?.a),
        b = copy(source?.b),
        c = copy(source?.c);
      if (!a || !b || !c) return null;
      const ab = axes.map((axis) => b[axis] - a[axis]),
        ac = axes.map((axis) => c[axis] - a[axis]);
      const area = Math.hypot(
        ab[1] * ac[2] - ab[2] * ac[1],
        ab[2] * ac[0] - ab[0] * ac[2],
        ab[0] * ac[1] - ab[1] * ac[0],
      );
      if (!(area > 1e-8)) return null;
      for (const point of [a, b, c])
        for (const axis of axes) {
          low[axis] = Math.min(low[axis], point[axis]);
          high[axis] = Math.max(high[axis], point[axis]);
        }
      triangles.push(Object.freeze({ id: `${part.id}-${index}`, a, b, c }));
    }
    const center = Object.fromEntries(axes.map((axis) => [axis, (low[axis] + high[axis]) / 2]));
    const radius = Math.hypot(...axes.map((axis) => (high[axis] - low[axis]) / 2));
    Object.freeze(triangles);
    if (!prepareSkyLeviathanMesh(triangles)) return null;
    compiled.push(
      Object.freeze({
        ...descriptor,
        restFrame: freezeFrame(frame),
        triangles,
        center: Object.freeze(center),
        radius,
      }),
    );
  }
  const handle = Object.freeze({ parts: Object.freeze(compiled), triangleCount: total });
  accepted.add(handle);
  return handle;
}

/** Map explicitly named absolute canonical driver frames to world art-joint
 * frames. Shared renderer/contact consumers use these same results. Drivers
 * own motion and limits; this function invents neither joint angles nor parent
 * policy. Missing mappings, invalid drivers or uncompiled handles return null. */
export function skyRigidDriverFrames(handle, drivers) {
  if (!accepted.has(handle) || !drivers) return null;
  const frames = {};
  for (const part of handle.parts) {
    if (!part.driverId || !part.driverToJoint) return null;
    const driver = Object.hasOwn(drivers, part.driverId) && skyFlightFrame(drivers[part.driverId]);
    if (!driver) return null;
    const local = part.driverToJoint;
    frames[part.id] = {
      origin: skyFlightPointToWorld(driver, local.origin),
      right: skyFlightVectorToWorld(driver, local.right),
      up: skyFlightVectorToWorld(driver, local.up),
      back: skyFlightVectorToWorld(driver, local.back),
    };
  }
  return frames;
}
